import {
  type QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { formatISO } from "date-fns/formatISO";
import { isValid } from "date-fns/isValid";
import orderBy from "lodash/orderBy";
import { useMemo } from "react";
import { useToast } from "~components/shared/useToast.tsx";
import { useCurrentTenant } from "~contexts/CurrentTenantContext/CurrentTenantContext";
import { useTranslation } from "~contexts/I18nContext/I18nContext.tsx";
import type { NewWorkPackage, WorkPackage } from "~generated";
import { ApiError, WorkPackagesService } from "~generated";
import { constructionSiteKeys } from "./constructionSites.ts";
import { allEmployeeKeys } from "./employees";
import type { QueryOpts } from "./helpers.ts";
import { managerKeys } from "./managerKeys.ts";
import { withOptionalFilters } from "./optionalFilters.ts";
import { teamAssignmentKeys } from "./teamAssignmentKeys.ts";
import { tenantKeys } from "./tenants";

const workPackageKeys = {
  base: (tenantId: string) =>
    [...tenantKeys.detail(tenantId), "workPackages", "v1"] as const,
  list: (
    tenantId: string,
    filters: {
      actualMinutesFromDate?: string;
      actualMinutesToDate?: string;
      constructionSiteManagerId?: string;
      active?: boolean;
    },
  ) =>
    withOptionalFilters(
      [...workPackageKeys.base(tenantId), "list"] as const,
      filters,
    ),
  detail: (tenantId: string, id: string) =>
    [...workPackageKeys.base(tenantId), "detail", id] as const,
};

async function getWorkPackages({
  tenantId,
  actualMinutesFromDateISO,
  actualMinutesToDateISO,
  constructionSiteManagerId,
  active,
}: {
  tenantId: string;
  actualMinutesFromDateISO?: string;
  actualMinutesToDateISO?: string;
  constructionSiteManagerId?: string;
  active?: boolean;
}): Promise<WorkPackage[]> {
  const workPackages = await WorkPackagesService.getWorkPackages(
    tenantId,
    actualMinutesFromDateISO,
    actualMinutesToDateISO,
    constructionSiteManagerId,
    active,
  );
  if (!workPackages?.work_packages) {
    return [];
  }
  return workPackages.work_packages;
}

type UseWorkPackageOptions = QueryOpts<
  WorkPackage[],
  ReturnType<typeof workPackageKeys.list>
>;
export function useWorkPackages(
  {
    actualMinutesFromDate,
    actualMinutesToDate,
    constructionSiteManagerId,
    active,
  }: {
    actualMinutesFromDate?: Date;
    actualMinutesToDate?: Date;
    constructionSiteManagerId?: string;
    active?: boolean;
  } = {},
  options?: Partial<UseWorkPackageOptions>,
) {
  const currentTenant = useCurrentTenant();

  const actualMinutesFromDateISO =
    actualMinutesFromDate && isValid(actualMinutesFromDate)
      ? formatISO(actualMinutesFromDate, { representation: "date" })
      : undefined;

  const actualMinutesToDateISO =
    actualMinutesToDate && isValid(actualMinutesToDate)
      ? formatISO(actualMinutesToDate, { representation: "date" })
      : undefined;

  return useQuery({
    ...options,
    queryKey: workPackageKeys.list(currentTenant.id, {
      actualMinutesFromDate: actualMinutesFromDateISO,
      actualMinutesToDate: actualMinutesToDateISO,
      constructionSiteManagerId,
      active,
    }),
    queryFn: (): Promise<WorkPackage[]> =>
      getWorkPackages({
        tenantId: currentTenant.id,
        actualMinutesFromDateISO,
        actualMinutesToDateISO,
        constructionSiteManagerId,
        active,
      }),
  });
}

type WorkPackageSorting = {
  orderField?: keyof WorkPackage;
  direction?: "asc" | "desc";
};

export function useSortedActiveWorkPackages({
  orderField,
  direction,
}: WorkPackageSorting = {}) {
  const {
    data: workPackages,
    isLoading,
    ...query
  } = useWorkPackages({
    active: true,
  });

  const sortedWorkPackages = useMemo(() => {
    if (!workPackages) {
      return undefined;
    }
    return orderBy(workPackages, orderField, direction ?? "asc");
  }, [workPackages, orderField, direction]);

  return { ...query, isLoading, data: sortedWorkPackages };
}

export function useWorkPackage(
  id?: string | null,
  {
    actualMinutesFromDate,
    actualMinutesToDate,
  }: { actualMinutesFromDate?: Date; actualMinutesToDate?: Date } = {},
  options?: Partial<UseWorkPackageOptions>,
) {
  const query = useWorkPackages(
    { actualMinutesFromDate, actualMinutesToDate },
    options,
  );
  return useMemo(
    () => ({
      ...query,
      data: query.data?.find((e) => e.id === id),
    }),
    [id, query],
  );
}

export function useWorkPackagesByConstructionSite(constructionSiteId: string) {
  const query = useWorkPackages({ active: true });
  return {
    ...query,
    data:
      query.data?.filter(
        (w) => w.construction_site_id === constructionSiteId,
      ) ?? [],
  };
}

export function useCreateWorkPackage() {
  const currentTenant = useCurrentTenant();
  const { showSuccessToast } = useToast();
  const t = useTranslation();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (workPackage: NewWorkPackage) =>
      WorkPackagesService.createWorkPackage(currentTenant.id, workPackage),

    onSuccess: async () => {
      showSuccessToast(t.addWorkPackageSuccess);
      await invalidate(queryClient, currentTenant.id);
    },
  });
  return mutation;
}

export function useEditWorkPackage(id: string) {
  const currentTenant = useCurrentTenant();
  const { showSuccessToast } = useToast();
  const t = useTranslation();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (workPackage: NewWorkPackage) =>
      WorkPackagesService.updateWorkPackage(currentTenant.id, id, workPackage),
    onSuccess: async () => {
      showSuccessToast(t.editSuccess);
      await invalidate(queryClient, currentTenant.id);
    },
  });
  return mutation;
}

export function useCreatePreparationWorkPackage() {
  const currentTenant = useCurrentTenant();
  const { showSuccessToast, showErrorToast } = useToast();
  const t = useTranslation();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (workPackageId: string) =>
      WorkPackagesService.createOrGetPreparationWorkPackage(
        currentTenant.id,
        workPackageId,
      ),
    onSuccess: async () => {
      showSuccessToast(t.createPreparationAssignmentSuccess);
      await invalidate(queryClient, currentTenant.id);
    },
    onError: (error) => {
      if (
        error instanceof ApiError &&
        error.status === 422 &&
        error.body?.errors?.wage_type
      ) {
        showErrorToast(t.createPreparationAssignmentNotAllowedError);
      } else {
        showErrorToast(t.unknownError);
      }
    },
  });
  return mutation;
}

async function invalidate(queryClient: QueryClient, currentTenantId: string) {
  await Promise.all([
    queryClient.invalidateQueries({
      queryKey: workPackageKeys.base(currentTenantId),
    }),
    queryClient.invalidateQueries({
      queryKey: teamAssignmentKeys.base(currentTenantId),
    }),
    queryClient.invalidateQueries({ queryKey: allEmployeeKeys.base() }),
    queryClient.invalidateQueries({
      queryKey: managerKeys.list(currentTenantId),
    }),
    queryClient.invalidateQueries({
      queryKey: constructionSiteKeys.base(currentTenantId),
    }),
  ]);
}
